import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, PlugZap, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import {
  MarketingHeader, MarketingFooter, MarketingBackdrop,
} from "@/components/MarketingShell";

/**
 * /electrical — indexable landing for the Electrical service line.
 * Content is generic and non-fabricated: installations, upgrades, panels,
 * EV-ready wiring — all confirmed capabilities.
 */
export default function ElectricalPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Electrical contracting",
    "name": "Electrical installations &amp; upgrades",
    "provider": { "@id": "https://www.powerbuilt.sx/#org" },
    "areaServed": [
      { "@type": "Country", "name": "Sint Maarten" },
      { "@type": "Country", "name": "Saint Martin" },
    ],
    "description": "Licensed electrical installations, panel upgrades, sub-panels, dedicated circuits and EV-ready wiring for St. Maarten homes and businesses.",
    "url": "https://www.powerbuilt.sx/electrical",
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",       "item": "https://www.powerbuilt.sx/" },
      { "@type": "ListItem", "position": 2, "name": "Electrical", "item": "https://www.powerbuilt.sx/electrical" },
    ],
  };
  return (
    <MarketingBackdrop>
      <SEO
        path="/electrical"
        title="Electrical Contractor in Sint Maarten — PowerBuilt"
        description="Licensed electrical installations, panel upgrades, sub-panels, dedicated circuits and EV-ready wiring for St. Maarten homes and businesses."
        jsonLd={{ "@context": "https://schema.org", "@graph": [jsonLd, breadcrumb] }}
      />
      <MarketingHeader />

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-8 md:pt-16 pb-16">
        <nav aria-label="Breadcrumb" className="text-xs text-white/50 mb-6">
          <Link to="/" className="hover:text-white">Home</Link> <span className="mx-1">/</span> <span className="text-white/70">Electrical</span>
        </nav>

        <section className="max-w-3xl">
          <p className="overline text-[#C9A356] mb-4">Service · Electrical</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight leading-[1.05]">
            Electrical work, <span className="text-[#C9A356]">done to code</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-lg">
            From a single dedicated circuit to a full panel upgrade, PowerBuilt handles licensed residential and light-commercial electrical work across Sint Maarten.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-accent text-base px-7 py-3" data-testid="electrical-cta-quote">
              Request an electrical quote <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </section>

        <section className="mt-16 border-t border-white/10 pt-12">
          <p className="overline text-[#C9A356] mb-3">What we do</p>
          <div className="grid md:grid-cols-3 gap-5 mt-4">
            {[
              { icon: Zap,     title: "Installations &amp; upgrades", copy: "New circuits, sub-panels, main panel upgrades, generator inlets and standby transfer setups." },
              { icon: PlugZap, title: "EV-ready wiring",              copy: "Dedicated 240V circuits and load-managed wiring so your home or garage is ready for an EV charger." },
              { icon: ShieldCheck, title: "Solar-adjacent electrical", copy: "AC-side wiring, isolators, surge protection and metering — the electrical work behind every good solar install." },
            ].map((f) => (
              <article key={f.title} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition hover:border-[#C9A356]/50">
                <div className="w-11 h-11 rounded-xl grid place-items-center mb-5 bg-[#C9A356]/15 text-[#C9A356] group-hover:bg-[#C9A356]/25 transition">
                  <f.icon className="w-5 h-5" />
                </div>
                <h2 className="font-heading text-xl mb-2" dangerouslySetInnerHTML={{ __html: f.title }} />
                <p className="text-sm text-slate-400 leading-relaxed">{f.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16 border-t border-white/10 pt-12">
          <p className="overline text-[#C9A356] mb-3">Related</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/solar"       className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">Solar &amp; battery storage &rarr;</Link>
            <Link to="/maintenance" className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">System maintenance &rarr;</Link>
            <Link to="/contact"     className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">Contact &rarr;</Link>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </MarketingBackdrop>
  );
}
